/**
 * BillingSuccess — landing page after Stripe Checkout completes.
 *
 * Stripe redirects here with ?session_id=cs_xxx. We poll
 * /api/billing/status/:session_id (max 5 attempts, 2s apart) until we see
 * payment_status = "paid", then surface the upgraded tier to the user.
 *
 * The backend is the source of truth — tier upgrade is applied server-side
 * on the first 'paid' status we see (idempotent). UI just reflects state.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Warning, CircleNotch, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_ATTEMPTS = 5;
const POLL_MS = 2000;

function BillingShell({ compass, children }) {
  if (compass === false) return <Layout>{children}</Layout>;
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-4 sm:px-6">
          <Link to="/compass" className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-md bg-emerald-800 font-bold text-white">A</div>
            <div>
              <div className="font-bold leading-none text-stone-900">Alaine's Compass<sup className="ml-0.5 text-[0.5em]">™</sup></div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-stone-500">Founder guided workspace</div>
            </div>
          </Link>
        </div>
      </header>
      {children}
    </div>
  );
}

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState({ phase: "polling", attempts: 0, data: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", attempts: 0, data: null, error: "Missing session_id" });
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          // Entitlement must be current before any Compass destination opens.
          await refreshUser();
          if (cancelled) return;
          setState({ phase: "success", attempts, data, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", attempts, data, error: null });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", attempts, data, error: null });
          return;
        }
        setState({ phase: "polling", attempts, data, error: null });
        setTimeout(poll, POLL_MS);
      } catch (err) {
        if (cancelled) return;
        setState({
          phase: "error", attempts, data: null,
          error: formatApiError(err?.response?.data?.detail) || "Couldn't verify payment",
        });
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const { phase, data, error, attempts } = state;
  const isCompass = data?.is_compass === true || (data?.tier_key || "").startsWith("compass_");
  const isGuideBundle = data?.tier_key === "compass_guides_annual";

  return (
    <BillingShell compass={data ? isCompass : undefined}>
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-16" data-testid="billing-success-page">
        <Card className="p-8 border-stone-200">

          {phase === "polling" && (
            <div data-testid="billing-status-polling">
              <div className="flex items-center gap-3">
                <CircleNotch size={28} className="text-emerald-700 animate-spin" weight="bold" />
                <h1 className="text-2xl font-black text-stone-900">Confirming your payment…</h1>
              </div>
              <p className="text-sm text-stone-600 mt-3 leading-relaxed">
                We're verifying your payment with Stripe. This usually takes a few seconds.
                Don't close this page.
              </p>
              <p className="text-[11px] font-mono text-stone-400 mt-4">
                Attempt {attempts} of {MAX_ATTEMPTS}
              </p>
            </div>
          )}

          {phase === "success" && (
            <div data-testid="billing-status-success">
              <div className="flex items-center gap-3">
                <CheckCircle size={32} className="text-emerald-700" weight="duotone" />
                <h1 className="text-2xl font-black text-stone-900">
                  {isCompass ? "Your Compass access is ready." : `You're upgraded to ${(data?.applied_tier || data?.tier_key || "").toString().toUpperCase()}.`}
                </h1>
              </div>
              <p className="text-sm text-stone-700 mt-3 leading-relaxed">
                {isCompass
                  ? "Your annual Compass plan is active. Open your guided workspace now."
                  : "Welcome to the upgraded plan. Your new formulation and ingredient limits are live on your account immediately."}
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3 text-[12px] font-mono">
                <div className="bg-stone-50 border border-stone-200 rounded px-3 py-2">
                  <div className="text-stone-500 uppercase tracking-wider text-[10px]">Amount</div>
                  <div className="text-stone-900 font-bold mt-0.5" data-testid="billing-amount">
                    ${((data?.amount_total || 0) / 100).toFixed(2)} {(data?.currency || "usd").toUpperCase()}
                  </div>
                </div>
                <div className="bg-stone-50 border border-stone-200 rounded px-3 py-2">
                  <div className="text-stone-500 uppercase tracking-wider text-[10px]">Tier</div>
                  <div className="text-stone-900 font-bold mt-0.5" data-testid="billing-tier">
                    {data?.is_compass
                      ? (data?.tier_key === "compass_guides_annual" ? "Compass + 3 Digital Guides — Annual" : "Individual — Annual")
                      : (data?.applied_tier || data?.tier_key || "—").toString()}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  <Link to={data?.is_compass ? "/compass" : "/dashboard"} data-testid="billing-go-dashboard">
                    {data?.is_compass ? "Open Compass" : "Open dashboard"} <ArrowRight size={14} className="ml-1.5" weight="bold" />
                  </Link>
                </Button>
                {isCompass && isGuideBundle && (
                  <Button asChild variant="outline">
                    <Link to="/compass?tab=guides" data-testid="billing-go-guides">Open My guides</Link>
                  </Button>
                )}
                <Button asChild variant="outline">
                  <Link to={isCompass ? "/compass" : "/pricing"} data-testid="billing-back-pricing">
                    {isCompass ? "Back to Compass" : "Back to pricing"}
                  </Link>
                </Button>
              </div>
            </div>
          )}

          {phase === "timeout" && (
            <div data-testid="billing-status-timeout">
              <div className="flex items-center gap-3">
                <Warning size={28} className="text-amber-700" weight="duotone" />
                <h1 className="text-2xl font-black text-stone-900">Still processing</h1>
              </div>
              <p className="text-sm text-stone-700 mt-3 leading-relaxed">
                Your payment is going through but Stripe hasn't confirmed it yet. We'll
                catch it via webhook — refresh this page in a minute or head to your
                 {isCompass ? "Compass" : "your dashboard"}. You'll see your new plan as soon as Stripe finalizes.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button onClick={() => window.location.reload()} className="bg-stone-900 hover:bg-stone-800 text-white">
                  Refresh
                </Button>
                <Button asChild variant="outline">
                   <Link to={isCompass ? "/compass" : "/dashboard"}>{isCompass ? "Go to Compass" : "Go to dashboard"}</Link>
                </Button>
              </div>
            </div>
          )}

          {phase === "expired" && (
            <div data-testid="billing-status-expired">
              <h1 className="text-2xl font-black text-stone-900">Checkout expired</h1>
              <p className="text-sm text-stone-700 mt-3">
                Your Stripe Checkout session expired before payment completed.
                No charge was made.
              </p>
              <Button asChild className="mt-5 bg-stone-900 hover:bg-stone-800 text-white">
                 <Link to={isCompass ? "/compass" : "/pricing"}>{isCompass ? "Back to Compass" : "Back to pricing"}</Link>
              </Button>
            </div>
          )}

          {phase === "error" && (
            <div data-testid="billing-status-error">
              <div className="flex items-center gap-3">
                <Warning size={28} className="text-rose-700" weight="duotone" />
                <h1 className="text-2xl font-black text-stone-900">Couldn't verify payment</h1>
              </div>
              <p className="text-sm text-stone-700 mt-3">{error}</p>
              <Button asChild className="mt-5 bg-stone-900 hover:bg-stone-800 text-white">
                 <Link to={isCompass ? "/compass" : "/pricing"}>{isCompass ? "Back to Compass" : "Back to pricing"}</Link>
              </Button>
            </div>
          )}

        </Card>
      </div>
    </BillingShell>
  );
}
